import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import api from '../../api';
import Loading from '../../components/Loading';
import CustomPagination from '../../components/Pagination';
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants/listPagination';
import { useFeatureFlag } from '../../helpers/featureFlags';
import { formatDate, presentPhone } from '../../helpers/formatters';
import { applyName } from '../../helpers/captureMode';
import { formatCalendarDate, isVisitManagedPartnerModality, localISODate, partnerModalityLabel, partnerStatusLabel } from './partnerLabels';

function PartnersListPage() {
    const { enabled, loading: flagLoading } = useFeatureFlag('commerce_partners');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [q, setQ] = useState('');
    const [modality, setModality] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(true);
    const [visitByPartner, setVisitByPartner] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const from = localISODate();
            const horizon = new Date();
            horizon.setDate(horizon.getDate() + 30);
            const [partnersResponse, calendarResponse] = await Promise.all([
                api.get('/commerce-partners', { params: { page, limit, q, modality, status } }),
                api.get('/commerce-partners/calendar', { params: { from, to: localISODate(horizon) } }),
            ]);
            setItems(partnersResponse?.data?.data || []);
            setTotal(partnersResponse?.data?.total || 0);
            const nextVisits = {};
            (calendarResponse?.data?.data || []).forEach((visit) => {
                if (visit.status === 'done' || !visit.partner_uuid) return;
                if (!nextVisits[visit.partner_uuid]) {
                    nextVisits[visit.partner_uuid] = visit;
                }
            });
            setVisitByPartner(nextVisits);
        } catch {
            setItems([]);
            setTotal(0);
            setVisitByPartner({});
        } finally {
            setLoading(false);
        }
    }, [page, limit, q, modality, status]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        load();
        return undefined;
    }, [enabled, load]);

    if (flagLoading) {
        return <Loading />;
    }
    if (!enabled) {
        return <Navigate to="/admin" replace />;
    }

    const totalPages = Math.max(1, Math.ceil(total / limit));

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/admin/partners" underline="hover" color="inherit">Parcerias</Link>
                <Typography color="text.primary">Lista</Typography>
            </Breadcrumbs>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }} spacing={1}>
                <Typography variant="h5">Parcerias comerciais</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <PageTutorialButtons pageKey="partners" />
                    <Button component={RouterLink} to="/admin/partners/calendar" variant="outlined">Calendário</Button>
                    <QuotaLimitedButton quotaKey="commerce_partners" to="/admin/partners/new" variant="contained">
                        Nova parceria
                    </QuotaLimitedButton>
                </Stack>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField label="Buscar" value={q} onChange={(e) => setQ(e.target.value)} fullWidth />
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Modalidade</InputLabel>
                        <Select label="Modalidade" value={modality} onChange={(e) => setModality(e.target.value)}>
                            <MenuItem value="">Todas</MenuItem>
                            <MenuItem value="pay_to_sell">Pay to sell</MenuItem>
                            <MenuItem value="consignment">Consignação</MenuItem>
                            <MenuItem value="repasse">Repasse (Comissão)</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="active">Ativa</MenuItem>
                            <MenuItem value="paused">Pausada</MenuItem>
                            <MenuItem value="ended">Encerrada</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="contained" onClick={() => { setPage(1); load(); }}>Buscar</Button>
                </Stack>
            </Paper>

            {loading ? <Loading /> : (
                <>
                    <TableContainer component={Paper}>
                        <Table sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '26%' }}>Estabelecimento</TableCell>
                                    <TableCell sx={{ width: '15%' }}>Modalidade</TableCell>
                                    <TableCell sx={{ width: '18%' }}>Produtos</TableCell>
                                    <TableCell sx={{ width: '10%' }}>Status</TableCell>
                                    <TableCell sx={{ width: '10%' }}>Próxima visita</TableCell>
                                    <TableCell align="right" sx={{ width: '21%' }}>Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => {
                                    const openVisit = visitByPartner[item.uuid];
                                    const productNames = (item.items || [])
                                        .map((product) => product.product_name)
                                        .filter(Boolean);
                                    return (
                                        <TableRow key={item.uuid} hover>
                                            <TableCell>
                                                <Typography
                                                    component={RouterLink}
                                                    to={`/admin/partners/${item.uuid}`}
                                                    fontWeight={600}
                                                    sx={{
                                                        color: 'primary.main',
                                                        textDecoration: 'none',
                                                        '&:hover': { textDecoration: 'underline' },
                                                    }}
                                                >
                                                    {applyName(item.name)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {[applyName(item.contact_name), presentPhone(item.contact_phone), item.city].filter(Boolean).join(' · ') || 'Sem responsável/telefone'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{partnerModalityLabel(item.modality)}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary" noWrap title={productNames.join(', ')}>
                                                    {productNames.length > 0 ? productNames.join(', ') : '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={0.5} alignItems="flex-start">
                                                    <Chip size="small" label={partnerStatusLabel(item.status)} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Desde {item.created_at ? formatDate(item.created_at, false) : '—'}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{openVisit?.planned_on ? formatCalendarDate(openVisit.planned_on) : '—'}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="nowrap">
                                                    {openVisit && isVisitManagedPartnerModality(item.modality) ? (
                                                        <Tooltip title="Informar visita">
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                component={RouterLink}
                                                                to={`/admin/partners/visits/${openVisit.uuid}`}
                                                                startIcon={<EventIcon fontSize="small" />}
                                                                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                                            >
                                                                + Visita
                                                            </Button>
                                                        </Tooltip>
                                                    ) : null}
                                                    <Tooltip title="Editar">
                                                        <IconButton size="small" component={RouterLink} to={`/admin/partners/${item.uuid}/edit`}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {items.length === 0 ? (
                                    <TableRow><TableCell colSpan={6}>Nenhuma parceria encontrada.</TableCell></TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <CustomPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                </>
            )}
        </Box>
    );
}

export default PartnersListPage;
